import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { z, ZodError } from 'zod/v4';
import { messageError } from '@/shared/ui/notifications';
import { ScrollAreaAutosize, Text, Title } from '@mantine/core';

type Accessor<T = unknown> = (res: AxiosResponse<any>) => T;

export function ValidateResponse<
  TSchema extends z.ZodType,
  TOut = z.infer<TSchema>,
>(
  schema: TSchema,
  options?: {
    accessor?: Accessor;
    merge?: (res: AxiosResponse<any>, parsed: TOut) => any;
    rethrow?: boolean;
    showMessage?: boolean;
  },
) {
  const {
    accessor,
    merge,
    rethrow = false,
    showMessage = true,
  } = options ?? {};
  return function <
    T extends (this: any, ...args: any[]) => Promise<AxiosResponse<unknown>>,
  >(target: any, _propertyKey: string, descriptor: TypedPropertyDescriptor<T>) {
    const original = descriptor.value!;
    descriptor.value = async function (this: any, ...args: Parameters<T>) {
      const res = await original.apply(this, args);
      const targetSegment = accessor ? accessor(res) : res.data;

      try {
        const parsed = schema.parse(targetSegment);

        const nextData = merge ? merge(res, parsed as TOut) : (parsed as TOut);
        return { ...res, data: nextData } as AxiosResponse<TOut>;
      } catch (err) {
        if (err instanceof ZodError) {
          const cfg: Partial<AxiosRequestConfig> = res.config ?? {};
          const zodMessage = z.prettifyError(err);
          const msg = `${zodMessage}\nAt ${cfg.method?.toUpperCase() ?? 'GET'} ${cfg.url ?? ''} [${res.status}]`;

          if (showMessage)
            messageError(
              <ScrollAreaAutosize
                data-testid='validation-error'
                mah={200}
                onClick={() => {
                  console.log(z.prettifyError(err));
                }}
              >
                <Title order={3}>Validation Error</Title>
                <Text component='pre'>{msg}</Text>
              </ScrollAreaAutosize>,
            );

          if (rethrow) throw new Error(msg.trim());
        }
        throw err;
      }
    } as T;
  };
}